import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Avatar,
  Box,
  Button,
  Chip,
  debounce,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  AddCircleOutline,
  Checklist,
  EditSquare,
  History,
  SearchOutlined,
} from "@mui/icons-material";

import { RootState } from "../../store/Index";
import { DataGrid } from "../../components/common/Index";
import { RoleItem } from "./components/SearchRoleDialog";
import { SearchRoleDialog } from "./components/SearchRoleDialog";

import { TrashIcon } from "../../components/icons";

import { ChecklistUsageProps } from "../../data/checklists";
import { tokens } from "../../theme";
import { getStatusColors } from "../../constants/statusColors";
import apiService from "../../service/Api";

// ---- Types aligned directly with backend naming (Checklist -> Section -> ListItem) ----

export interface ListItemPayload {
  id?: number;
  name: string;
  description?: string | null;
  points?: number;
  order?: number;
}

export interface SectionPayload {
  id?: number;
  name: string;
  description?: string | null;
  order: number;
  list_items: ListItemPayload[];
}

export type PhaseValue = "pre_stream" | "on_stream" | "post_stream";

export interface ChecklistRow {
  id: number;
  name: string;
  description: string | null;
  roleId: number | null;
  roleName: string;
  phase: PhaseValue;
  sections: SectionPayload[];
  updatedAt: string;
}

interface ChecklistListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ChecklistApiResponse[];
}

interface ChecklistApiResponse {
  id: number;
  name: string;
  description?: string | null;
  roles?: number | null;
  roles_name?: string;
  phase?: PhaseValue;
  sections?: SectionPayload[];
  updated_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const PHASE_LABELS: Record<PhaseValue, string> = {
  pre_stream: "Pre-Stream",
  on_stream: "On-Stream",
  post_stream: "Post-Stream",
};

const Checklists: React.FC = () => {
  const [searchRoleOpen, setSearchRoleOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">(
    "all",
  );

  const [checklistData, setChecklistData] = useState<ChecklistRow[]>([]);
  const [usageData] = useState<ChecklistUsageProps[]>([]);

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const navigate = useNavigate();

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const statusColors = getStatusColors(theme.palette.mode);

  const primaryColor = colors.primary[400];
  const primaryTextColor = colors.primary[400];
  const secondaryTextColor = colors.neutral[100];
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const fetchChecklists = useCallback(async () => {
    try {
      const response = await apiService.get<ChecklistListResponse>(
        "/api/checklists/checklist/",
      );

      const rows: ChecklistRow[] = response.data.results.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description ?? null,
        roleId: item.roles ?? null,
        roleName: item.roles_name || "Unknown",
        phase: item.phase || "pre_stream",
        sections: item.sections ?? [],
        updatedAt: item.updated_at,
      }));

      setChecklistData(rows);
    } catch (error) {
      console.error("Checklist fetch error:", error);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  const handleDeleteChecklist = async (checklistId: number) => {
    try {
      await apiService.delete(`/api/checklists/checklist/${checklistId}/`);
      setChecklistData((prev) =>
        prev.filter((item) => item.id !== checklistId),
      );
    } catch (error) {
      console.error("Delete checklist failed:", error);
    }
  };

  const filteredChecklists = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return checklistData.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(term) ||
        item.roleName.toLowerCase().includes(term) ||
        PHASE_LABELS[item.phase].toLowerCase().includes(term);

      // No "active/inactive" concept exists on the backend yet — treat everything
      // as active until that field is added, so the filter doesn't hide real data.
      const matchesStatus = filterType === "all" || filterType === "active";

      return matchesSearch && matchesStatus;
    });
  }, [checklistData, searchTerm, filterType]);

  const filteredUsage = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return usageData.filter((item) => {
      return (
        item.event.name.toLowerCase().includes(term) ||
        item.checklist_title.toLowerCase().includes(term) ||
        item.pilot.name.toLowerCase().includes(term)
      );
    });
  }, [usageData, searchTerm]);

  const [editChecklist, setEditChecklist] = useState<ChecklistRow | null>(null);

  const getApplicationChipProps = (phase: PhaseValue) => {
    switch (phase) {
      case "pre_stream":
        return { label: "Pre-Stream", bgcolor: "#F79E1B40", color: "#F79E1B" };
      case "on_stream":
        return {
          label: "On-Stream",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      case "post_stream":
        return { label: "Post-Stream", bgcolor: "#4BA66340", color: "#4BA663" };
      default:
        return { label: "Unknown", bgcolor: "#BDBDBD40", color: "#BDBDBD" };
    }
  };

  const getStatusChipProps = (status: string) => {
    switch (status.toLowerCase()) {
      case "complete":
        return { label: "Complete", bgcolor: "#4BA66340", color: "#4BA663" };
      case "partial":
        return { label: "Partial", bgcolor: "#F79E1B40", color: "#F79E1B" };
      case "pending":
        return {
          label: "Pending",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      default:
        return {
          label: "Unknown",
          bgcolor: statusColors.pending.bg,
          color: statusColors.pending.fg,
        };
    }
  };

  const getComplianceProps = (compliance: number) => {
    if (compliance >= 80) return "#4BA663";
    if (compliance >= 65) return "#0000FF";
    if (compliance >= 50) return "#F79E1B";
    return "#EB4335";
  };

  const checklistsColumns: GridColDef<ChecklistRow>[] = [
    {
      field: "name",
      headerName: "Checklist",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "roleName",
      headerName: "Role & Phase",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 0.4 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.roleName}
          </Typography>
          <Chip
            label={getApplicationChipProps(params.row.phase).label}
            sx={{
              color: getApplicationChipProps(params.row.phase).color,
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: getApplicationChipProps(params.row.phase).bgcolor,
            }}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: () => (
        <Box
          display="flex"
          alignItems="center"
          sx={{ lineHeight: 1, py: 1 }}
          gap={1}
        >
          <Switch
            checked
            color="primary"
            slotProps={{ input: { "aria-label": "controlled" } }}
          />
          <Typography variant="caption">Active</Typography>
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Update",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography variant="subtitle2">
            {format(new Date(params.row.updatedAt), "MMM dd, yyyy")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "sections",
      headerName: "Sections",
      flex: 1,
      renderCell: (params) => {
        const itemCount = params.row.sections.reduce(
          (sum, s) => sum + (s.list_items?.length ?? 0),
          0,
        );
        return (
          <Box sx={{ py: 1, lineHeight: 1 }}>
            <Typography variant="subtitle2">
              {params.row.sections.length} sections
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {itemCount} tasks
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          sx={{ py: 1, lineHeight: 1 }}
          gap={1}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setEditChecklist(params.row);
              setSearchRoleOpen(true);
            }}
          >
            <EditSquare color="primary" sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleDeleteChecklist(params.row.id);
            }}
          >
            <TrashIcon color="error" sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const usageColumns: GridColDef<ChecklistUsageProps>[] = [
    {
      field: "event",
      headerName: "Event/Stream",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.row.event.name}
          </Typography>
          <Typography variant="caption" color="#757575" fontWeight="medium">
            {params.row.event.type}
          </Typography>
        </Box>
      ),
    },
    {
      field: "checklist",
      headerName: "Checklist",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 0.4 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.checklist_title}
          </Typography>
        </Box>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography variant="subtitle2">
            {format(new Date(params.row.date), "MMM dd, yyyy")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "pilot",
      headerName: "Crew Member",
      flex: 1,
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          sx={{ lineHeight: 1, py: 1 }}
          gap={1}
        >
          <Avatar
            src={""}
            sx={{
              width: 32,
              height: 32,
              bgcolor: colors.primary[800],
              color: colors.primary[100],
            }}
          />
          <Box>
            <Typography variant="subtitle2">{params.row.pilot.name}</Typography>
            <Typography color="#757575" variant="caption">
              {params.row.pilot.role}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "completion",
      headerName: "Completion",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: getComplianceProps(params.row.completion) }}
          >{`${params.row.completion}%`}</Typography>
          <Typography variant="caption">
            {`${params.row.tasks_completed}/${params.row.tasks} tasks`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" sx={{ py: 1.6, lineHeight: 1 }}>
          <Chip
            label={getStatusChipProps(params.row.status).label}
            sx={{
              color: getStatusChipProps(params.row.status).color,
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: getStatusChipProps(params.row.status).bgcolor,
            }}
            size="small"
          />
        </Box>
      ),
    },
  ];

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleRowClick = (params: GridRowParams<ChecklistRow>) => {
    navigate(`/crew-management/checklists/${params.row.id}`, {
      state: { checklist: params.row },
    });
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Checklist sx={{ fontSize: 20 }} />
                <Typography variant="body2">All Checklists</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <History sx={{ fontSize: 20 }} />
                <Typography variant="body2">Usage History</Typography>
              </Box>
            }
          />
        </Tabs>
      </Box>

      <AnimatePresence mode="wait">
        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              height: "100%",
              background: colors.primary[900],
              padding: "24px",
              borderRadius: 5,
              boxShadow: 0,
            }}
          >
            <Box
              display="flex"
              mb="16px"
              alignItems="center"
              justifyContent="space-between"
              gap="4px"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: backgroundColor,
                    border: "1px solid",
                    borderRadius: 2,
                    borderColor: "primary",
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    flex: 1,
                    minWidth: 320,
                  }}
                >
                  <SearchOutlined
                    sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }}
                  />
                  <InputBase
                    placeholder="Search by name, role or phase..."
                    sx={{ padding: 0.4, flex: 1, color: primaryTextColor }}
                    onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                  />
                </Box>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel
                    id="checklist-status-label"
                    sx={{ color: primaryTextColor }}
                  >
                    Checklist Status
                  </InputLabel>
                  <Select
                    labelId="checklist-status-label"
                    id="checklist-status-label"
                    value={filterType}
                    label="Checklist Status"
                    onChange={(e) =>
                      setFilterType(
                        e.target.value as "all" | "active" | "inactive",
                      )
                    }
                    sx={{
                      background: backgroundColor,
                      color: primaryTextColor,
                      borderRadius: 2,
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryColor,
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                size="small"
                startIcon={<AddCircleOutline />}
                sx={{ textTransform: "capitalize" }}
                variant="contained"
                onClick={() => {
                  setEditChecklist(null);
                  setSearchRoleOpen(true);
                }}
              >
                Add Checklist
              </Button>

              <SearchRoleDialog
                searchRoleOpen={searchRoleOpen}
                setSearchRoleOpen={setSearchRoleOpen}
                searchResults={selectedRole}
                setSearchResults={setSelectedRole}
                refreshChecklists={fetchChecklists}
                editChecklist={editChecklist}
                mode={editChecklist ? "edit" : "add"}
              />
            </Box>

            <Box>
              <DataGrid
                rows={filteredChecklists}
                columns={checklistsColumns}
                rowCount={filteredChecklists.length}
                paginationMode="server"
                paginationModel={paginationModel}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              height: "100%",
              background: colors.primary[900],
              padding: "24px",
              borderRadius: 5,
              boxShadow: 0,
            }}
          >
            <Box
              display="flex"
              mb="16px"
              alignItems="center"
              justifyContent="space-between"
              gap="4px"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: backgroundColor,
                    border: "1px solid",
                    borderRadius: 2,
                    borderColor: "primary",
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    flex: 1,
                    minWidth: 320,
                  }}
                >
                  <SearchOutlined
                    sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }}
                  />
                  <InputBase
                    placeholder="Search by name, role or usage..."
                    sx={{ padding: 0.4, flex: 1, color: primaryTextColor }}
                    onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                  />
                </Box>
              </Box>
              <Button
                size="small"
                startIcon={<AddCircleOutline />}
                sx={{ textTransform: "capitalize" }}
                variant="contained"
              >
                Add Checklist
              </Button>
            </Box>

            <Box>
              <DataGrid
                rows={filteredUsage}
                columns={usageColumns}
                rowCount={filteredUsage.length}
                paginationMode="server"
                paginationModel={paginationModel}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </TabPanel>
      </AnimatePresence>
    </Box>
  );
};

export default Checklists;
